import {
    Alignment, Api, DisplayType, LicenseTester, McLeodClassicIntegration, ModelRow, StringUtil
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    Button, Component, DataSource, Label, Layout, SlideoutDecorator, Snackbar, Textbox, Checkbox,
    DataSourceMode
} from "@mcleod/components";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { PartnerProfileNotes } from "./PartnerProfileNotes";
import { ModelEdilocation } from "./models/ModelEdilocation";
import { ModelEdiorderProfile } from "./models/ModelEdiorderProfile";
import { ModelEdiCustomer } from "./models/ModelEdiCustomer";
import { ModelEdiControlShared } from "./models/ModelEdiControlShared";
import { EdiProfileBillingQuickInfo } from "./EdiProfileBillingQuickInfo";
import { TransmissionStatusQuickInfo } from "./TransmissionStatusQuickInfo";
import { CustomerQuickInfo } from "@mcleod/ar";
import { EdiPartner } from "./EdiPartner";
import { ModelEdilocNojoins } from "./models/ModelEdilocNojoins";

function tooltipSetCallback(component: Component, modelRow: ModelRow, quickInfoLayout: string, forRecovery: boolean) {
    CommonTooltips.setTooltipContentLayout(component, null, quickInfoLayout, null,
        {
            "id": modelRow.get("partner_id_output"),
            "log_id": forRecovery ? modelRow.get("edi_log_id") : modelRow.get("edi_log.id")
        }
    );
    component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
}

export async function isIntercompanyEnabled(): Promise<boolean> {
    const dfControlRow = await new ModelEdiControlShared().searchSingle();
    if (dfControlRow != null) {
        const icEnabled = dfControlRow.getBoolean("intercompany_enabled", false);
        const icLicensed = LicenseTester.testClass("com.tms.client.loadmaster.dsp.PanelOrderIntercompany");
        return icEnabled && icLicensed;
    }
    else {
        return false;
    }
}

export function setTemplateValues(modelRow: ModelRow<any>, mainDataSource: DataSource) {
    const activeRow = mainDataSource?.activeRow;
    if (!activeRow) {
        return;
    }
    activeRow.set("version", modelRow.get("version"));
    activeRow.set("transaction_set", modelRow.get("transaction_set"));
    if (activeRow.get("direction") === "I") {
        activeRow.set("partner_id", modelRow.get("app_sender_id"));
        activeRow.set("alt_partner_id", modelRow.get("sender_id"));
    } else {
        activeRow.set("partner_id", modelRow.get("app_receiver_id"));
        activeRow.set("alt_partner_id", modelRow.get("receiver_id"));
    }
}

export function setFuncAckTemplateValues(modelRow: ModelRow<any>, mainDataSource: DataSource) {
    const activeRow = mainDataSource?.activeRow;
    if (!activeRow) {
        return;
    }
    activeRow.set("func_version", modelRow.get("version"));
    if (activeRow.get("direction") === "O") {
        activeRow.set("func_partner_id", modelRow.get("app_sender_id"));
        activeRow.set("func_altpartner_id", modelRow.get("sender_id"));
    } else {
        activeRow.set("func_partner_id", modelRow.get("app_receiver_id"));
        activeRow.set("func_altpartner_id", modelRow.get("receiver_id"));
    }
}

export function setReplyTemplateValues(modelRow: ModelRow<any>, mainDataSource: DataSource) {
    const activeRow = mainDataSource?.activeRow;
    if (!activeRow) {
        return;
    }
    activeRow.set("reply_version", modelRow.get("version"));
    if (activeRow.get("direction") === "O") {
        activeRow.set("reply_partner_id", modelRow.get("app_sender_id"));
        activeRow.set("reply_altpartnerid", modelRow.get("sender_id"));
    } else {
        activeRow.set("reply_partner_id", modelRow.get("app_receiver_id"));
        activeRow.set("reply_altpartnerid", modelRow.get("receiver_id"));
    }
}

export function setBatchCodeTooltip(
    component: Component,
    row: ModelRow<any>,
    transaction_type: string,
    position: Alignment = Alignment.TOP
) {
    if (!component || !row) return;
    const sourceID = row.get("id");
    const direction = row.get("direction");
    const partnerId = row.get("partner_id");
    const altPartnerId = row.get("alt_partner_id");
    const batch = row.get("batch");
    const version = row.get("version");

    if (sourceID != null && direction === "O") {
        CommonTooltips.setTooltipContentLayout(
            component,
            null,
            "lme/datafusion/EdiBatchHistoryQuickInfoTable",
            null,
            {
                "edi_batch_history.transaction_id": sourceID,
                "edi_batch_history.transaction_type": transaction_type,
                "partner_id": partnerId,
                "alt_partner_id": altPartnerId
            }
        );
        component.tooltipDynamicOptions = { position };
    } else if (sourceID != null && direction === "I") {
        CommonTooltips.setTooltipContentLayout(
            component,
            null,
            "lme/datafusion/EdiInboundLogQuickInfoTable",
            null,
            {
                "direction": direction,
                "batch": batch,
                "func_sender_id": partnerId,
                "sender_id": altPartnerId,
                "func_version": version,
                "transaction_type": transaction_type
            }
        );
        component.tooltipDynamicOptions = { position };
    }
}

/**
 * Sets up partner name quick info tooltip for EDI billing components
 * @param textboxPartnerName The textbox component to add the tooltip to
 * @param sourceRow The data source row containing EDI billing details
 */
export function setPartnerNameQuickInfo(textboxPartnerName: Textbox | Label, sourceRow: ModelRow) {
    textboxPartnerName.tooltipDynamicContent = async () => {
        const partnerID = sourceRow?.get("edibilling_profile.partner_id");
        const altPartnerID = sourceRow?.get("edibilling_profile.alt_partner_id");
        const version = sourceRow?.get("edibilling_profile.version");
        const direction = sourceRow?.get("edibilling_profile.direction");
        const transaction_set = sourceRow?.get("edibilling_profile.transaction_set");
        const batch = sourceRow?.get("batch");

        if (partnerID != null && altPartnerID != null && version != null && direction != null && transaction_set != null) {
            const layout = await Layout.getLoadedLayout("lme/datafusion/EdiProfileBillingQuickInfo") as EdiProfileBillingQuickInfo;
            await layout.sourceEdibillingProfile?.search({ partner_id: partnerID, alt_partner_id: altPartnerID, version: version, direction: direction, transaction_set: transaction_set, batch: batch ?? 0 });
            return layout;
        }
        return null;
    };
}
export function setCustomerQuickInfo(textboxCustomer: Textbox | Label, row: ModelRow) {
    textboxCustomer.tooltipDynamicContent = async () => {
        const customerID = row?.get("customer_id");
        const layout = await Layout.getLoadedLayout("lme/ar/CustomerQuickInfo") as CustomerQuickInfo;
        await layout.sourceCustomer?.search({ id: customerID });
        return layout;
    }
}
/**
 * Creates a customization slideout for EDI fields
 * @param layoutPath The path to the layout to load
 * @param title The title for the slideout
 * @param dataSource The data source containing the active row
 * @param fieldName The field name to set on the active row
 * @param maskPropertyName The property name on the layout for the mask (e.g., 'fileMask', 'mask')
 * @param isImageType Whether this is for image type layouts
 * @param batchNo The batch number to set on the layout
 */
export function createCustomizationSlideout(
    layoutPath: string,
    title: string,
    dataSource: DataSource<ModelRow<any>>,
    fieldName: string,
    maskPropertyName: string,
    isImageType: boolean = false,
    batchNo: number = 0
) {
    const layout = Layout.getLayout(layoutPath) as any;
    layout.batchno = batchNo;

    // Set batch number if provided
    if (layout.batchno !== undefined) {
        layout.batchno = batchNo;
    }

    const generateButton = new Button({
        caption: "Save & Close",
        rowBreak: false,
        color: "primary.reverse",
        backgroundColor: "primary",
        onClick: async () => {
            const mask = layout.generateMask ? layout.generateMask() : layout.generateFileMask();

            if (layout.validateForbiddenCharacters()) {
                dataSource.activeRow?.set(fieldName, mask);
                layout.slideOut();
            }
        }
    });

    const clearButton = new Button({
        caption: "Clear",
        rowBreak: false,
         color: "primary.reverse",
        backgroundColor: "secondary",
        onClick: () => {
            if (layout.buttonClearOnClick) {
                layout.buttonClearOnClick({ target: clearButton });
            }
        }
    });

    if (layoutPath === "lme/datafusion/EdiCustomFilename") {
        layout.isImageType = isImageType;
    }
    const sod = new SlideoutDecorator({
        layout,
        title,
        width: '45%',
        fillVerticalSpace: true,
        addlComponents: [clearButton, generateButton],
        headerProps: {},
        doAfterSlideIn: async () => {
            layout[maskPropertyName] = dataSource?.activeRow?.get(fieldName);
        }
    });

    sod.slideIn({ speed: 200 });
}

/**
 * Sets up transmission log quick info tooltip for EDI components
 * @param component The component to add the tooltip to
 * @param dataSourceRow The data source row containing EDI details
 * @param isInbound Whether this is for inbound or outbound transmission
 */
export function generateTransmissionLogQuickInfo(component: Component, dataSourceRow: ModelRow, isInbound: boolean) {
    component.tooltipDynamicContent = async () => {
        if (dataSourceRow?.get("batch") > "0") {
            const layout = await Layout.getLoadedLayout("lme/datafusion/TransmissionStatusQuickInfo") as TransmissionStatusQuickInfo;
            if (layout.sourceEdiLog !== null) {
                const searchParameters = isInbound ? {
                    direction: "I",
                    batch: dataSourceRow.get("batch"),
                    func_sender_id: dataSourceRow.get("partner_id"),
                    sender_id: dataSourceRow.get("alt_partner_id"),
                    func_version: dataSourceRow.get("version"),
                    transaction_type: 'I'
                } :
                    {
                        direction: "O",
                        batch: dataSourceRow.get("batch"),
                        func_receiver_id: dataSourceRow.get("partner_id"),
                        receiver_id: dataSourceRow.get("alt_partner_id"),
                        func_version: dataSourceRow.get("version"),
                        transaction_type: 'I'
                    }
                await layout.sourceEdiLog.search(searchParameters);
            }
            return layout;
        }
        return null;
    };
}

export class EdiUtility {
    public static getErrorProps() {
        return {
            imageName: ImageName.circleX,
            imageColor: "error"
        };
    }

    public static getSuccessProps() {
        return {
            imageName: ImageName.circleCheck,
            imageColor: "success"
        };
    }

    /**
     * Extracts a clean, user-friendly error message from an API error response.
     * Specifically handles "currently dispatching" errors and other InvalidDataException messages
     * by extracting just the message portion after the exception class name.
     *
     * @param error The error object from an API call (typically contains a messages array)
     * @returns A clean error message suitable for display to the user
     */
    public static getCleanErrorMessage(error: any): string {
        if (!error) {
            return "An unknown error occurred.";
        }

        // If error is already a string, check if it needs cleaning
        if (typeof error === "string") {
            return EdiUtility.extractMessageFromException(error);
        }

        // Handle error objects with messages array (typical API error structure)
        if (error.messages && Array.isArray(error.messages)) {
            // Look through messages for specific exception patterns
            for (const message of error.messages) {
                if (typeof message === "string") {
                    // Check for "currently dispatching" pattern specifically
                    if (message.includes("currently dispatching")) {
                        return EdiUtility.extractMessageFromException(message);
                    }
                    // Check for InvalidDataException pattern
                    if (message.includes("InvalidDataException:")) {
                        return EdiUtility.extractMessageFromException(message);
                    }
                }
            }
            // If no specific pattern found, return the last message (usually the most specific)
            const lastMessage = error.messages[error.messages.length - 1];
            if (typeof lastMessage === "string") {
                return EdiUtility.extractMessageFromException(lastMessage);
            }
        }

        // Handle error objects with a message property
        if (error.message && typeof error.message === "string") {
            return EdiUtility.extractMessageFromException(error.message);
        }

        // Fallback: convert to string
        return String(error);
    }

    /**
     * Extracts the message portion from an exception string.
     * Handles patterns like "com.tms.common.lib.data.InvalidDataException: actual message here"
     * Also strips any stack trace that may be appended to the message.
     *
     * @param exceptionString The full exception string
     * @returns The extracted message portion, or the original string if no pattern matched
     */
    private static extractMessageFromException(exceptionString: string): string {
        if (!exceptionString) {
            return "";
        }

        let result = exceptionString;

        // Pattern: "com.package.ExceptionClass: message"
        // We want to extract just the message after the colon
        const colonIndex = result.indexOf(": ");
        if (colonIndex !== -1) {
            // Check if the part before the colon looks like an exception class name
            const beforeColon = result.substring(0, colonIndex);
            if (beforeColon.includes("Exception") || beforeColon.includes("Error")) {
                result = result.substring(colonIndex + 2).trim();
            }
        }

        // Strip stack trace - look for common stack trace patterns
        // Stack traces typically start with "\n\tat " or "\n\tcom." or just "\tat "
        const stackTracePatterns = ["\n\tat ", "\tat com.", "\n\tcom.", "\ncom.tms"];
        for (const pattern of stackTracePatterns) {
            const stackIndex = result.indexOf(pattern);
            if (stackIndex !== -1) {
                result = result.substring(0, stackIndex).trim();
                break;
            }
        }

        return result;
    }

    public static getPartnerNameTooltipForTransmission(component: Component, modelRow: ModelRow, forRecovery: boolean) {
        if (modelRow.get("partner_name") !== "Profile Not Found" && !StringUtil.isEmptyString(modelRow.get("partner_id_output"))) {
            const transactionType = modelRow.get("partner_type_output");
            switch (transactionType) {
                case "T":
                    return tooltipSetCallback(component, modelRow, "lme/datafusion/EdiProfileOrderQuickInfo", forRecovery);
                case "S":
                    return tooltipSetCallback(component, modelRow, "lme/datafusion/EdiProfileStatusQuickInfo", forRecovery);
                case "A":
                    return tooltipSetCallback(component, modelRow, "lme/datafusion/EdiProfileRemitQuickInfo", forRecovery);
                case "I":
                    return tooltipSetCallback(component, modelRow, "lme/datafusion/EdiProfileBillingQuickInfo", forRecovery);
                case "Z":
                    return tooltipSetCallback(component, modelRow, "lme/datafusion/EdiProfileGenericQuickInfo", forRecovery);
            }
        } else if (forRecovery) {
            return CommonTooltips.setTooltipContentTextboxes(component, () => [
                { caption: "Partner ID", displayType: DisplayType.STRING, text: modelRow.get("partner_id") },
                { caption: "Alt Partner ID", displayType: DisplayType.STRING, text: modelRow.get("alt_partner_id") }
            ], false);
        } else {
            return CommonTooltips.setTooltipContentTextboxes(component, () => [
                { caption: "Int Sender ID", displayType: DisplayType.STRING, text: modelRow.get("edi_log.sender_id") },
                { caption: "Int Receiver ID", displayType: DisplayType.STRING, text: modelRow.get("edi_log.receiver_id") },
                { caption: "Func Sender ID", displayType: DisplayType.STRING, text: modelRow.get("edi_log.func_sender_id") },
                { caption: "Func Receiver ID", displayType: DisplayType.STRING, text: modelRow.get("edi_log.func_receiver_id") }
            ], false);
        }
    }

    public static showPartnerNotes(id: string, partnerName: string, notes: string) {
        if (!id) return;
        const partnerProfileNotes = Layout.getLayout("lme/datafusion/PartnerProfileNotes", {
            maxWidth: 900,
            width: window.innerWidth * .60,
        }) as PartnerProfileNotes;
        partnerProfileNotes.notes = notes;
        new SlideoutDecorator({
            layout: partnerProfileNotes,
            title: `Partner Notes (${partnerName})`,
            fillVerticalSpace: true
        });
    }

    public static openClassicPartnerProfile(id: string, classicScreen: string) {
        McLeodClassicIntegration.openClassicScreen(classicScreen, "<ext> search id='" + id + "'");
    }

    public static async promptForXref(mainDataSource: DataSource<ModelRow<any>>, orderSource: DataSource<ModelRow<any>>, stopSource: DataSource<ModelRow<any>>, isEdiStop: boolean = false): Promise<any> {
        const stopId = mainDataSource.data[0].data["id"];
        const stopType = mainDataSource.data[0].data["stop_type"];
        const orderId = orderSource.data[0].data["id"];
        const shipId = stopSource?.data[0]?.data["id"];
        const consId = stopSource?.data[stopSource?.data?.length - 1]?.data["id"];
        let profileId = orderSource["ediProfileId"];
        if (StringUtil.isEmptyString(profileId)) {
            let version = "";
            const partner = orderSource.data[0].data["partner_id"];

            version = isEdiStop ? orderSource.data[0].data["partner_version"] : orderSource.data[0].data["version"];
            if (isEdiStop && orderSource.data[0].data["ordered_method"] === "M" && orderSource.data[0].data["partner_version"] !== undefined) {
                version = orderSource.data[0].data["partner_version"];
            }

            if (!StringUtil.isEmptyString(partner) && !StringUtil.isEmptyString(version)) {
                const profile = await new ModelEdiorderProfile().searchSingle({
                    partner_id: partner,
                    version: version
                });
                profileId = profile?.data?.id;
            }
        }

        return Api.search("lme/datafusion/xref-profile", {
            stop_id: stopId,
            stop_type: stopType,
            order_id: orderId,
            profile_id: profileId,
            cons_id: consId,
            ship_id: shipId,
            is_edi_stop: isEdiStop ? "Y" : "N"
        });
    }

    public static async addDataFusionCrossReference(partnerId: string, version: string, locId: string, ediLoc: string): Promise<any> {
        return Api.post("lme/datafusion/add-location-xref", {
            "partner_id": partnerId,
            "version": version,
            "edi_location": ediLoc,
            "location_id": locId
        });
    }

    public static returnYesNoDialogForExistingReference(ediLocationFromRow: string, locId: string, ediLoc: string) {
        return CommonDialogs.showYesNo(`Cross reference exists for partner code ${ediLocationFromRow} and location ${locId} on the corresponding DataFusion profile.  Would you still like to add this cross reference (${ediLoc}/${locId})?`,
            "Add DataFusion Cross Reference",
            { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } });
    }

    public static returnYesNoDialogForExistingReferenceUpdate(ediLocationFromRow: string, locId: string, ediLoc: string) {
        return CommonDialogs.showYesNo(`Cross reference exists for partner code ${ediLoc} and location ${ediLocationFromRow} on the corresponding DataFusion profile.  Would you like to update this cross reference (${ediLoc}/${locId})?`,
            "Add DataFusion Cross Reference",
            { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } });
    }

    public static returnYesNoDialogForAddingCrossReference(ediLoc: string, locId: string) {
        return CommonDialogs.showYesNo(`Would you like to add a cross reference for partner code ${ediLoc} and location ${locId} on the corresponding DataFusion profile(s)?`,
            "Add DataFusion Cross Reference",
            { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } });
    }

    public static setLabelXref(labelXref: Label, xrefExists: boolean) {
        if (xrefExists) {
            labelXref.visible = true;
            labelXref.imageName = ImageName.cross_ref_confirmed;
            labelXref.imageColor = "default.darkest";
            labelXref.tooltip = "Location cross reference successfully applied";
        } else {
            labelXref.visible = true;
            labelXref.imageName = ImageName.cross_ref_missing;
            labelXref.imageColor = "default.darkest";
            labelXref.tooltip = "Location is missing a cross reference to DataFusion location.";
        }
    }

    public static async setXref(currentRow: ModelRow, dfXref: Label) {
        if (!StringUtil.isEmptyString(currentRow.get("edi_loc_code"))) {
            if (!StringUtil.isEmptyString(currentRow.get("location_id"))) {
                const edilocRow = await new ModelEdilocation().searchSingle({
                    location_id: currentRow.get("location_id"),
                    edi_location: currentRow.get("edi_loc_code")
                });
                EdiUtility.setLabelXref(dfXref, edilocRow == null ? false : true);
            }
            else {
                EdiUtility.setLabelXref(dfXref, false);
            }
        }
        else {
            dfXref.visible = false;
        }
    }

    public static async checkDFXref(locId: string, ediLoc: string, mainDataSource: DataSource<ModelRow<any>>, orderSource: DataSource<ModelRow<any>>, xrefLabel: Label, owner: any, stopSource: DataSource<ModelRow<any>>, isEdiStop: boolean = false) {
        let edilocRow = null;
        const partnerFromOrderSource = orderSource.data[0].data["partner_id"];
        const versionFromOrderSource = isEdiStop ? orderSource.data[0].data["version"] : orderSource.data[0].data["partner_version"];
        if (!StringUtil.isEmptyString(ediLoc)
            && !StringUtil.isEmptyString(locId)
            && !StringUtil.isEmptyString(partnerFromOrderSource)
            && !StringUtil.isEmptyString(versionFromOrderSource)) {
            edilocRow = await new ModelEdilocation().searchSingle({ location_id: locId, edi_location: ediLoc, partner_id: partnerFromOrderSource, version: versionFromOrderSource });
        }

        if (edilocRow == null) {
            EdiUtility.promptForXref(mainDataSource, orderSource, stopSource, isEdiStop).then(async result => {
                const needsPrompt: boolean = result.data[0].needs_xref_prompt == "Y" ? true : false;
                const partnerId: string = result.data[0].partner_id;
                const version: string = result.data[0].version;
                const edilocRow2 = await new ModelEdilocation().searchSingle({ location_id: locId, partner_id: partnerId, version: version });  //xref exists for same location id if present
                const edilocRow3 = await new ModelEdilocNojoins().searchSingle({ edi_location: ediLoc, partner_id: partnerId, version: version });  //xref exists for same edi location if present

                //Duplicate checks - allow for multiple partner codes to xref to same location_id, but do not allow the same partner code to point to multiple location_ids
                if (needsPrompt && edilocRow2 != null) {
                    //Check for multiple xref for same LME location id - warn but allow multiples
                    if (edilocRow2.get("edi_location") == ediLoc) {
                        return;
                    }
                    else if (!StringUtil.isEmptyString(edilocRow2.get("edi_location")) && !StringUtil.isEmptyString(locId) && !StringUtil.isEmptyString(ediLoc)) {
                        EdiUtility.returnYesNoDialogForExistingReference(edilocRow2.get("edi_location"), locId, ediLoc).then(yes => {
                            if (yes) {
                                //add additional xref to LME location id
                                return EdiUtility.addDataFusionCrossReference(partnerId, version, locId, ediLoc);
                            }
                            else {
                                return;
                            }
                        });
                    }
                }
                else if (needsPrompt && edilocRow3 != null) {
                    //Check for multiple xref for same partner code - only allow update
                    if (edilocRow3.get("location_id") == locId) {
                        return "Record Exists";
                    }
                    else if (!StringUtil.isEmptyString(edilocRow3.get("location_id")) && !StringUtil.isEmptyString(locId) && !StringUtil.isEmptyString(ediLoc)) {
                        EdiUtility.returnYesNoDialogForExistingReferenceUpdate(edilocRow3.get("location_id"), locId, ediLoc).then(yes => {
                            if (yes) {
                                //update existing xref - update edi_location
                                edilocRow3.set("location_id", locId);
                                edilocRow3.post();
                            }
                            else {
                                return "Record Exists";
                            }
                        });
                    }
                }
                else if (needsPrompt) {
                    if (!StringUtil.isEmptyString(ediLoc)) {
                        if (!StringUtil.isEmptyString(locId)) {
                            CommonDialogs.showYesNo("Would you like to add a cross reference for partner code " + ediLoc + " and location " + locId +
                                " on the corresponding DataFusion profile(s)?", "Add DataFusion Cross Reference",
                                {
                                    titleProps: {
                                        imageName: ImageName.circleX,
                                        imageColor: "primary.light"
                                    }
                                }).then(async yes => {
                                    if (yes) {
                                        EdiUtility.addDataFusionCrossReference(partnerId, version, locId, ediLoc).then(result => {
                                            const recAdded = result.data[0].result;

                                            if (recAdded == "Record Added") {
                                                Snackbar.showSnackbar("Successfully added cross reference for " + ediLoc + " and " + locId + ".", { targetPanel: owner });
                                                EdiUtility.setLabelXref(xrefLabel, true);
                                            }
                                            else {
                                                Snackbar.showWarningSnackbar("Unable to add cross reference for " + ediLoc + " and " + locId + ".", { targetPanel: owner });
                                                EdiUtility.setLabelXref(xrefLabel, false);
                                            }
                                        });
                                    }
                                    else {
                                        EdiUtility.setLabelXref(xrefLabel, false);
                                    }
                                });
                        }
                        else {
                            EdiUtility.setLabelXref(xrefLabel, false);
                        }
                    }
                    else if (!StringUtil.isEmptyString(locId) && !StringUtil.isEmptyString(ediLoc)) {
                        EdiUtility.setLabelXref(xrefLabel, edilocRow == null ? false : true);
                    }
                }
            });
        }
        else {
            EdiUtility.setLabelXref(xrefLabel, true);
        }
    }

    public static async dfXrefOnClick(mainDataSource: DataSource<ModelRow<any>>, orderSource: DataSource<ModelRow<any>>, dfXref: Label, owner: any, stopSource?: DataSource<ModelRow<any>>) {
        const locId = mainDataSource.data[0].data["location_id"];
        const ediLoc = mainDataSource.data[0].data["edi_loc_code"];
        const partnerFromOrderSource = orderSource.data[0].data["partner_id"];
        const versionFromOrderSource = orderSource.data[0].data["version"] ?? orderSource.data[0].data["partner_version"];

        const edilocRow = await new ModelEdilocation().searchSingle({ location_id: locId, edi_location: ediLoc, partner_id: partnerFromOrderSource, version: versionFromOrderSource });
        if (edilocRow != null
            && edilocRow.get("location_id") == locId
            && edilocRow.get("edi_location") == ediLoc
            && edilocRow.get("partner_id") == partnerFromOrderSource
            && edilocRow.get("version") == versionFromOrderSource
        ) {
            EdiUtility.setLabelXref(dfXref, true);
            return;
        }

        EdiUtility.promptForXref(mainDataSource, orderSource, stopSource, true).then(async result => {
            let partnerId: string = result.data[0].partner_id;
            let version: string = result.data[0].version;

            const labelXref = dfXref;

            if (!StringUtil.isEmptyString(locId) && !StringUtil.isEmptyString(ediLoc)) {
                const edilocRow2 = await new ModelEdilocation().searchSingle({ location_id: locId, partner_id: partnerId, version: version });  //xref exists for same location id if present
                const edilocRow3 = await new ModelEdilocNojoins().searchSingle({ edi_location: ediLoc, partner_id: partnerId, version: version });  //xref exists for same edi location if present

                //Duplicate checks - allow for multiple partner codes to xref to same location_id, but do not allow the same partner code to point to multiple location_ids
                if (edilocRow2 != null) {
                    //Check for multiple xref for same LME location id - warn but allow multiples
                    if (edilocRow2.get("edi_location") == ediLoc) {
                        return;
                    }
                    else if (!StringUtil.isEmptyString(edilocRow2.get("edi_location")) && !StringUtil.isEmptyString(locId) && !StringUtil.isEmptyString(ediLoc)) {
                        EdiUtility.returnYesNoDialogForExistingReference(edilocRow2.get("edi_location"), locId, ediLoc).then(yes => {
                            if (yes) {
                                //add additional xref to LME location id
                                return EdiUtility.addDataFusionCrossReference(partnerId, version, locId, ediLoc).then(() => {
                                    EdiUtility.setLabelXref(dfXref, true);
                                });

                            }
                            else {
                                return;
                            }
                        });
                    }
                }
                else if (edilocRow3 != null) {
                    //Check for multiple xref for same partner code - only allow update
                    if (edilocRow3.get("location_id") == locId) {
                        return;
                    }
                    else if (!StringUtil.isEmptyString(edilocRow3.get("location_id")) && !StringUtil.isEmptyString(locId) && !StringUtil.isEmptyString(ediLoc)) {
                        EdiUtility.returnYesNoDialogForExistingReferenceUpdate(edilocRow3.get("location_id"), locId, ediLoc).then(yes => {
                            if (yes) {
                                //update existing xref - update edi_location
                                edilocRow3.set("location_id", locId);
                                edilocRow3.post();
                                EdiUtility.setLabelXref(dfXref, true);
                            }
                            else {
                                return "Record Exists";
                            }
                        });
                    }
                }
                else {
                    EdiUtility.returnYesNoDialogForAddingCrossReference(ediLoc, locId).then(async yes => {
                        if (yes) {
                            const custId = orderSource.data[0].data["customer_id"];
                            if (partnerId === undefined && version === undefined
                                && orderSource.data[0].data["ordered_method"] === "M") {
                                const ediCustomerResult = await new ModelEdiCustomer().search({ "customer_id": custId });
                                partnerId = ediCustomerResult.modelRows[0].data["edi_customer.partner_id"];
                                version = ediCustomerResult.modelRows[0].data["edi_customer.version"];
                            }

                            if (!StringUtil.isEmptyString(locId) && !StringUtil.isEmptyString(ediLoc)) {
                                EdiUtility.addDataFusionCrossReference(partnerId, version, locId, ediLoc).then(result => {
                                    const recAdded = result.data[0].result;

                                    if (recAdded == "Record Added") {
                                        Snackbar.showSnackbar("Successfully added cross reference for " + ediLoc + " and " + locId + ".", { targetPanel: owner });
                                        EdiUtility.setLabelXref(dfXref, true);
                                    }
                                    else {
                                        Snackbar.showWarningSnackbar("Unable to add cross reference for " + ediLoc + " and " + locId + ".", { targetPanel: owner });
                                        EdiUtility.setLabelXref(dfXref, false);
                                    }
                                });
                            }
                            else {
                                labelXref.visible = false;
                            }
                        }
                    });
                }
            }
            else {
                Snackbar.showWarningSnackbar("Location ID and DataFusion partner code must be present to create a cross reference.")
            }
        });
    }

    public static isLogisticsLicensed(): boolean {
        return LicenseTester.testClass("com.tms.client.loadmaster.edi.EntryEdiTenderStatusControl");
    }

    private static readonly SPECIAL_CHAR_MAP = EdiUtility.createSpecialCharMap();

    private static createSpecialCharMap(): Map<number, string> {
        const map = new Map<number, string>();
        map.set(0, "NUL");
        map.set(1, "SOH");
        map.set(2, "STX");
        map.set(3, "ETX");
        map.set(4, "EOT");
        map.set(5, "ENQ");
        map.set(6, "ACK");
        map.set(7, "BEL");
        map.set(8, "BS");
        map.set(9, "HT");
        map.set(10, "NL");
        map.set(11, "VT");
        map.set(12, "NP");
        map.set(13, "CR");
        map.set(14, "SO");
        map.set(15, "SI");
        map.set(16, "DLE");
        map.set(17, "DC1");
        map.set(18, "DC2");
        map.set(19, "DC3");
        map.set(20, "DC4");
        map.set(21, "NAK");
        map.set(22, "SYN");
        map.set(23, "ETB");
        map.set(24, "CAN");
        map.set(25, "EM");
        map.set(26, "SUB");
        map.set(27, "ESC");
        map.set(28, "FS");
        map.set(29, "GS");
        map.set(30, "RS");
        map.set(31, "US");
        map.set(32, "SP");

        map.set(128, "PAD");
        map.set(129, "HOP");
        map.set(130, "BPH");
        map.set(131, "NBH");
        map.set(132, "IND");
        map.set(133, "NEL");
        map.set(134, "SSA");
        map.set(135, "ESA");
        map.set(136, "HTS");
        map.set(137, "HTJ");
        map.set(138, "VTW");
        map.set(139, "PLD");
        map.set(140, "PLU");
        map.set(141, "RI");
        map.set(142, "SS2");
        map.set(143, "SS3");
        map.set(144, "DCS");
        map.set(145, "PU1");
        map.set(146, "PU2");
        map.set(147, "STS");
        map.set(148, "CCH");
        map.set(149, "MW");
        map.set(150, "SPA");
        map.set(151, "EPA");
        map.set(152, "SOS");
        map.set(153, "SGCI");
        map.set(154, "SCI");
        map.set(155, "CSI");
        map.set(156, "ST");
        map.set(157, "OSC");
        map.set(158, "PM");
        map.set(159, "APC");
        return map;
    }

    public static async findOriginalOrderId(ediOrderRow: ModelRow): Promise<any> {
        return await Api.search("lme/datafusion/find-original-order-id", { change_tender_id: ediOrderRow.get("id") });
    }

    public static getAsciiChar(asciiValue: number): string {
        if (asciiValue < 0 || asciiValue > 255) {
            return "";
        }

        if (asciiValue <= 32 || (asciiValue >= 128 && asciiValue <= 159)) {
            return EdiUtility.SPECIAL_CHAR_MAP.get(asciiValue) || "";
        }

        if (asciiValue === 127) {
            return "DEL";
        }

        return String.fromCharCode(asciiValue);
    }
}

/**
 * Handles master batch checkbox changes by checking for existing master batch setup
 * and creating one if needed
 * @param checkbox The checkbox component that was changed
 * @param partnerName The partner name
 * @param partnerID The partner ID
 * @param altPartnerId The alternate partner ID
 * @param version The version
 * @returns Promise<void>
 */
export async function handleMasterBatchCheck(
    checkbox: Checkbox,
    partnerName: string,
    partnerID: string,
    altPartnerId: string,
    version: string
): Promise<void> {
    if (checkbox.checked) {
        try {
            const result = await Api.search("lme/datafusion/edi-partner", {
                partner_id: partnerID,
                alt_partner_id: altPartnerId,
                version: version
            });

            if (result.data.length > 0) {
                checkbox.checked = true;
            } else {
                const shouldCreate = await CommonDialogs.showYesNo(
                    "No master batch setup detected, would you like to create one?",
                    "Create Master Batch"
                );

                if (shouldCreate) {
                    // Logic to create master batch
                    const masterBatch = Layout.getLayout("lme/datafusion/EdiPartner", {
                        width: window.innerWidth * 0.35,
                        backgroundColor: "defaultBackground",
                        fillHeight: true,
                        paddingBottom: 50
                    }) as EdiPartner;

                    checkbox.checked = true;
                    new SlideoutDecorator({
                        layout: masterBatch,
                        title: "Master Batch Setup",
                        fillVerticalSpace: true,
                        addlComponents: [masterBatch.saveButton],
                        doAfterSlideIn: () => {
                            // Create new ModelRow and add to masterBatch.mainDataSource
                            const newRow = new ModelRow("lme/datafusion/edi-partner", true);
                            newRow.set("alt_partner_id", altPartnerId);
                            newRow.set("partner_id", partnerID);
                            newRow.set("version", version);
                            newRow.set("partner_name", partnerName);
                            newRow.set("next_batch", 1);
                            masterBatch.mainDataSource.addRow(newRow);
                            masterBatch.mainDataSource.rowIndex = masterBatch.mainDataSource.data.length - 1;

                            masterBatch.saveButton.enabled = true;

                            masterBatch.saveButton.addClickListener(() => {
                                // Logic to execute when the save button is clicked
                                masterBatch.mainDataSource.post().then(() => {
                                    masterBatch.slideOut();
                                });
                            });
                        },
                        onClose: () => {
                            // Logic to execute when the slide-out is closed
                            checkbox.checked = false;
                            masterBatch.slideOut();
                        }
                    });
                } else {
                    checkbox.checked = false;
                }
            }
        } catch (error) {
            console.error("Error checking master batch setup:", error);
            checkbox.checked = false;
        }
    }
}

/**
 * Handles master batch button clicks by opening the master batch setup slideout
 * @param partnerName The partner name
 * @param partnerID The partner ID
 * @param altPartnerId The alternate partner ID
 * @param version The version
 * @param onSaveCallback Optional callback function to execute after save
 * @returns Promise<void>
 */
export async function handleButtonMasterBatchClick(
    partnerName: string,
    partnerID: string,
    altPartnerId: string,
    version: string,
    onSaveCallback?: () => void
): Promise<void> {
    try {
        // Logic to create master batch
        const masterBatch = Layout.getLayout("lme/datafusion/EdiPartner", {
            width: window.innerWidth * 0.35,
            backgroundColor: "defaultBackground",
            fillHeight: true,
            paddingBottom: 50
        }) as EdiPartner;

        new SlideoutDecorator({
            layout: masterBatch,
            title: "Master Batch Setup",
            fillVerticalSpace: true,
            addlComponents: [masterBatch.saveButton],
            doAfterSlideIn: async () => {
                // Create new ModelRow and add to masterBatch.mainDataSource
                const result = await masterBatch.mainDataSource.search({
                    partner_id: partnerID,
                    alt_partner_id: altPartnerId,
                    version: version
                });
                if (result.modelRows.length === 0) {
                    const ediPartnerRow = new ModelRow("lme/datafusion/edi-partner", true);
                    ediPartnerRow.set("alt_partner_id", altPartnerId);
                    ediPartnerRow.set("partner_id", partnerID);
                    ediPartnerRow.set("version", version);
                    ediPartnerRow.set("partner_name", partnerName);
                    ediPartnerRow.set("next_batch", 1);
                    masterBatch.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [ediPartnerRow]);
                }
                masterBatch.saveButton.enabled = true;
                masterBatch.saveButton.addClickListener(() => {
                    // Logic to execute when the save button is clicked
                    masterBatch.mainDataSource.post().then(() => {
                        if (onSaveCallback) {
                            onSaveCallback();
                        }
                        masterBatch.slideOut();
                    });
                });
            },
            onClose: () => {
                masterBatch.slideOut();
            }
        });
    } catch (error) {
        console.error("Error handling master batch button click:", error);
    }
}
